import {
    PreziBriefFields,
    PreziBriefFields_inspirationFiles,
    PreziBriefFields_referenceFiles,
} from '__generated__/PreziBriefFields';
import { PreziProjectType, PreziTimeframe } from '__generated__/types';
import Attachments from 'projects/components/Attachments';
import { formatAndLinkify } from 'projects/components/FormatAndLinkify';
import { linkify } from 'projects/components/Linkify';
import SummaryCard from 'projects/components/summary/SummaryCard';
import SummaryCardItem from 'projects/components/summary/SummaryCardItem';
import { TimeframeSummary } from 'projects/components/TimeFrame';
import { trackEditBriefEvent, trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import { Industries } from 'projects/util/industries';
import React, { useEffect } from 'react';

interface Fields {
    profileApproval: boolean;
    projectType: PreziProjectType;
    existingPreziUrl: string;
    preziClassicUrl: string;
    language: string;
    description: string;
    industry: string;
    inspirationUrls: string[];
    other: string;
    startDate: any | null;
    endDate: any | null;
    timeframe: PreziTimeframe;
    title: string;
    urls: string[];
    inspirationFiles: PreziBriefFields_inspirationFiles[];
    referenceFiles: PreziBriefFields_referenceFiles[];
}

interface Props<T> {
    brief: {
        id: string;
        fields: PreziBriefFields;
        designers: { displayName: string }[];
    };
    industries: Industries;
}

const projectType = (projectType: PreziProjectType) => {
    switch (projectType) {
        case PreziProjectType.BRANDED_PREZI_TEMPLATE:
            return 'Create a branded Prezi template';
        case PreziProjectType.CONVERT_CLASSIC_TO_NEXT:
            return 'Convert a Prezi Classic to Prezi Next';
        case PreziProjectType.CONVERT_PPT_TO_PREZI:
            return 'Convert a PowerPoint presentation to Prezi';
        case PreziProjectType.EDIT_EXISTING_PREZI:
            return 'Edit an existing Prezi';
        case PreziProjectType.NEW_CUSTOM_PREZI:
            return 'Create a new custom Prezi';
        case PreziProjectType.OTHER:
            return 'Other';
        default:
            return projectType;
    }
};

const SummaryPage: <T extends Fields>(props: Props<T>) => React.ReactElement<Props<T>> = ({
    brief,
    industries,
}) => {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Summary',
        });
    }, []);
    return (
        <div className="content-setting">
            <SummaryCard
                heading={__('Tell us about your project')}
                onEditClick={() =>
                    trackEditBriefEvent({
                        id: brief.id,
                        page: 'Details',
                    })
                }
                url={routes.brief(brief, 1)}
            >
                <SummaryCardItem title={__('Name your project')}>
                    {brief.fields.title}
                </SummaryCardItem>
                <SummaryCardItem title={__('What best describes your need?')}>
                    {projectType(brief.fields.projectType)}
                </SummaryCardItem>
                {brief.fields.existingPreziUrls.length > 0 && (
                    <SummaryCardItem title={__('Link to any existing Prezi')}>
                        {linkify(brief.fields.existingPreziUrls)}
                    </SummaryCardItem>
                )}
                {brief.fields.preziClassicUrls.length > 0 && (
                    <SummaryCardItem title={__('Link to any Prezi Classic')}>
                        {linkify(brief.fields.preziClassicUrls)}
                    </SummaryCardItem>
                )}
                {brief.fields.powerpointFiles.length > 0 && (
                    <SummaryCardItem title={__('Add PowerPoint files')}>
                        {Attachments(brief.fields.powerpointFiles)}
                    </SummaryCardItem>
                )}
                <SummaryCardItem title={__('Describe your project')}>
                    {formatAndLinkify(brief.fields.description)}
                </SummaryCardItem>
                {brief.fields.referenceFiles.length > 0 && (
                    <SummaryCardItem title={__('Add reference files')}>
                        {Attachments(brief.fields.referenceFiles)}
                    </SummaryCardItem>
                )}
                {brief.fields.inspirationFiles.length > 0 && (
                    <SummaryCardItem title={__('Add inspiration files')}>
                        {Attachments(brief.fields.inspirationFiles)}
                    </SummaryCardItem>
                )}
                {brief.fields.inspirationUrls.length > 0 && (
                    <SummaryCardItem title={__('Link to any inspiration on the Web')}>
                        {linkify(brief.fields.inspirationUrls)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
            <SummaryCard
                heading={__('Your timeline')}
                onEditClick={() =>
                    trackEditBriefEvent({
                        id: brief.id,
                        page: 'TimeAndBudget',
                    })
                }
                url={routes.brief(brief, 3)}
            >
                <SummaryCardItem title={__("What's your timeline?")}>
                    <TimeframeSummary
                        value={brief.fields.timeframe}
                        startDate={brief.fields.startDate}
                        endDate={brief.fields.endDate}
                    />
                </SummaryCardItem>
            </SummaryCard>
            <SummaryCard
                heading={__('About you')}
                onEditClick={() =>
                    trackEditBriefEvent({
                        id: brief.id,
                        page: 'AboutYou',
                    })
                }
                url={routes.brief(brief, 2)}
            >
                {industries.find(brief.fields.industry) && (
                    <SummaryCardItem title={__('What industry are you in?')}>
                        {industries.find(brief.fields.industry)}
                    </SummaryCardItem>
                )}
                {brief.fields.urls.length > 0 && (
                    <SummaryCardItem title={__('Add your website and/or social media pages')}>
                        {linkify(brief.fields.urls)}
                    </SummaryCardItem>
                )}
                <SummaryCardItem
                    title={__(
                        'Can your designer showcase the finished designs in their 99designs portfolio?'
                    )}
                >
                    {brief.fields.profileApproval ? __('Yes') : __('No')}
                </SummaryCardItem>
                {brief.fields.other != '' && (
                    <SummaryCardItem
                        title={__("Anything else you'd like to share with your designer?")}
                    >
                        {formatAndLinkify(brief.fields.other)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        </div>
    );
};

export default SummaryPage;
